import { useCallback, useMemo, useState } from 'react';

import {
  METRIC,
  type Depth,
  type StatisticData,
  type TargetVolume,
  type ThemeStatisticData,
  type ViewBy,
} from '@/entities/insight';
import {
  PostFilter,
  Sentiment,
  type PostFilterMap,
  type ThemeTabType,
} from '../../../../entities/post/models';
import { useFilteredPostsNavigation } from '../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../filtered-profiles/hook/use-filtered-profiles-navigation';
import { getNextDimension } from '../../shared/helpers/get-next-dimension';
import { showDrillDownContextMenu } from '../overlay/show-drill-down-context-menu';

interface UnifiedControllerState {
  depths: Depth[];
  viewBy: ViewBy;
  metric: METRIC;
  selectedTab: ThemeTabType;
}

// Use Depth type directly as it now includes sentiment

export const useUnifiedThemeTableController = () => {
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();

  const [state, setState] = useState<UnifiedControllerState>({
    depths: [],
    viewBy: PostFilter.Theme,
    metric: METRIC.MENTION,
    selectedTab: 'all',
  });

  const setMetric = useCallback((metric: METRIC) => {
    setState((prev) => ({ ...prev, metric }));
  }, []);

  const setSelectedTab = useCallback((tab: ThemeTabType) => {
    setState((prev) => ({ ...prev, selectedTab: tab }));
  }, []);

  const addDepth = useCallback(
    (depth: Depth, sentiment?: string) => {
      const newDepth: Depth = {
        ...depth,
        sentiment: sentiment || state.depths[0]?.sentiment,
      };
      setState((prev) => ({ ...prev, depths: [...prev.depths, newDepth] }));
    },
    [state.depths],
  );

  const toPrevDepth = useCallback(() => {
    setState((prev) => ({
      ...prev,
      depths: prev.depths.slice(0, -1),
    }));
  }, []);

  const clearDepths = useCallback(() => {
    setState((prev) => ({ ...prev, depths: [] }));
  }, []);

  const setViewBy = useCallback((viewBy: ViewBy) => {
    setState((prev) => ({ ...prev, viewBy }));
  }, []);

  const currentDepth: Depth | null = useMemo(() => {
    if (state.depths.length === 0) return null;
    return state.depths[state.depths.length - 1];
  }, [state.depths]);

  const getCurrentLevel = useCallback((): 'theme' | 'brand' | 'product' => {
    if (state.depths.length === 0) return 'theme';
    const nextDimension = getNextDimension(currentDepth?.dimension || null);
    if (nextDimension === PostFilter.Brand) return 'brand';
    if (nextDimension === PostFilter.Product) return 'product';
    return 'theme';
  }, [currentDepth, state.depths.length]);

  const filter = useMemo(() => {
    const filter: PostFilterMap = {};

    state.depths.forEach((depth) => {
      filter[depth.dimension] = depth.value ?? undefined;
    });

    // Add sentiment from the first depth (theme level) if it exists
    const themeSentiment = state.depths[0]?.sentiment;
    if (themeSentiment) {
      filter[PostFilter.Sentiment] = themeSentiment;
    }

    return filter;
  }, [state.depths]);

  const targetVolume: TargetVolume = useMemo(() => {
    switch (state.metric) {
      case METRIC.MENTION:
        return 'mention_volume';
      case METRIC.POST:
        return 'post_volume';
    }
  }, [state.metric]);

  const handleRowClick = useCallback(
    async (statistic: StatisticData | ThemeStatisticData, event: React.MouseEvent) => {
      const action = await showDrillDownContextMenu({
        position: { x: event.clientX, y: event.clientY },
      });

      const targetDimension = getNextDimension(currentDepth?.dimension || null);
      const additionalFilter = targetDimension ? { [targetDimension]: statistic.name } : {};

      // Get sentiment from the row data (for theme level) or from depths
      const sentiment = 'sentiment' in statistic ? statistic.sentiment : currentDepth?.sentiment;

      const finalFilter = {
        ...(sentiment && { [PostFilter.Sentiment]: sentiment }),
        ...filter,
        ...additionalFilter,
      };

      switch (action) {
        case 'view-posts':
          await navigateToFilteredPosts({
            dimension: PostFilter.Theme,
            filter: finalFilter,
          });
          break;
        case 'view-profiles':
          await navigateToFilteredProfiles({
            dimension: PostFilter.Theme,
            filter: finalFilter,
          });
          break;
        case 'close':
          break;
      }
    },
    [currentDepth, filter, navigateToFilteredPosts, navigateToFilteredProfiles],
  );

  const handleDrillDown = useCallback(
    (statistic: StatisticData | ThemeStatisticData) => {
      const targetDimension = getNextDimension(currentDepth?.dimension || null);
      if (!targetDimension) return;

      // Extract sentiment if it's a theme row
      const sentiment = 'sentiment' in statistic ? statistic.sentiment : currentDepth?.sentiment;

      addDepth(
        {
          dimension: targetDimension,
          value: statistic.name,
          sentiment,
        },
        sentiment,
      );
    },
    [currentDepth, addDepth],
  );

  // Get current sentiment for API calls based on depths
  const getCurrentSentiment = useCallback((): Sentiment | undefined => {
    // At theme level, we don't pass sentiment to API
    if (state.depths.length === 0) return undefined;

    // For drill-down levels, use the sentiment from the first depth
    const sentiment = state.depths[0]?.sentiment;
    if (sentiment === 'positive') return Sentiment.Positive;
    if (sentiment === 'negative') return Sentiment.Negative;
    return undefined;
  }, [state.depths]);

  return {
    state,
    setState,
    addDepth,
    toPrevDepth,
    clearDepths,
    setMetric,
    setViewBy,
    setSelectedTab,
    currentDepth,
    getCurrentLevel,
    filter,
    targetVolume,
    handleRowClick,
    handleDrillDown,
    getCurrentSentiment,
  };
};
